import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { BiCopy, BiLinkExternal, BiRefresh } from 'react-icons/bi';
import { FaBoxOpen, FaCheckCircle, FaCreditCard, FaExclamationTriangle, FaMoneyBillWave, FaTruck, FaUndo } from 'react-icons/fa';
import Breadcrumbs from '../../components/BreadCrumbs';
import Card from '../../components/cards/Card';
import CustomLoading from '../../components/customLoading/CustomLoading';
import DateRangePicker from '../../components/inputFields/DateRangePicker';
import Dropdown from '../../components/inputFields/DropDown';
import SearchBar from '../../components/searchBar/SearchBar';
import Table from '../../components/tables/Table';
import Pagination from '../../components/tables/pagination/Pagination';
import { getShippingTrackingDashboard, patchShipment } from '../../services/shipment';
import { patchOrder } from '../../services/orders';
import { canTransitionToDispatched } from '../../utils/orderLogistics';

const STATUS_OPTIONS = [
    { label: 'All', value: 'all' },
    { label: 'Confirmed', value: 'CONFIRMED' },
    { label: 'In Packaging', value: 'IN_PACKAGING' },
    { label: 'Ready To Dispatch', value: 'READY_TO_DISPATCH' },
    { label: 'Dispatched', value: 'DISPATCHED' },
    { label: 'Delivered', value: 'DELIVERED' },
    { label: 'Returned', value: 'RETURNED' },
    { label: 'Cancelled', value: 'CANCELLED' },
];

/** Same options / colours as B2C Orders — uses `patchOrder` on shop-order CRUD. */
const ORDER_STATUS_DROPDOWN_OPTIONS = [
    { label: 'Pending', value: 'PENDING' },
    { label: 'Confirmed', value: 'CONFIRMED' },
    { label: 'Packaging', value: 'IN_PACKAGING' },
    { label: 'Ready to Dispatch', value: 'READY_TO_DISPATCH' },
    { label: 'Dispatched', value: 'DISPATCHED' },
    { label: 'Delivered', value: 'DELIVERED' },
    { label: 'Cancelled', value: 'CANCELLED' },
    { label: 'Returned', value: 'RETURNED' },
];

const STATUS_LABELS = [...ORDER_STATUS_DROPDOWN_OPTIONS, ...STATUS_OPTIONS].reduce(
    (acc, { value, label }) => {
        if (value && acc[value] == null) acc[value] = label;
        return acc;
    },
    {}
);

const getToday = () => new Date().toISOString().split('T')[0];

const getFirstDayOfMonth = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
};

const formatDays = (days) => {
    const value = Number(days);
    if (!Number.isFinite(value)) return null;
    return `${value} ${value === 1 ? 'day' : 'days'}`;
};

/** `delivery_rate` from API = logistics cost (2 decimals). */
const formatInr2 = (n) => {
    const num = Number(n);
    const safe = Number.isFinite(num) ? num : 0;
    return `₹${safe.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Backend: calendar days from order_date (local) to delivery_date; null if missing. */
const renderTotalDaysToDeliverLine = (value) => {
    if (value == null || value === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return (
        <span className="text-xs text-gray-500 leading-tight block">
            {n} {n === 1 ? 'day' : 'days'} (order → delivery)
        </span>
    );
};

const getOrderStatusDropdownColor = (status) => {
    switch (status) {
        case 'PENDING': return '#f59e0b';
        case 'CONFIRMED': return '#3b82f6';
        case 'IN_PACKAGING': return '#8b5cf6';
        case 'READY_TO_DISPATCH': return '#06b6d4';
        case 'DISPATCHED': return '#f97316';
        case 'DELIVERED': return '#22c55e';
        case 'CANCELLED': return '#ef4444';
        case 'RETURNED': return '#9ca3af';
        default: return '#ffffff';
    }
};

function DeliveryDateEditor({ logisticId, orderId, initialDate, onSaved }) {
    const existing = initialDate != null && String(initialDate).trim() !== '';
    const [date, setDate] = useState('');
    const [saving, setSaving] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => {
        setDate('');
    }, [initialDate, logisticId]);

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
    }, []);

    const persistDeliveryDate = useCallback(async (nextDate) => {
        if (!orderId || !logisticId) {
            toast.error('Missing order or shipment id');
            return;
        }
        if (!nextDate) return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('order', orderId);
            fd.append('delivery_date', nextDate);
            const res = await patchShipment(fd, logisticId);
            if (res.status === 200 || res.status === 201) {
                toast.success('Delivery date saved');
                onSaved?.();
            } else {
                toast.error('Could not save delivery date');
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.response?.data?.error || 'Could not save delivery date';
            toast.error(typeof msg === 'string' ? msg : 'Could not save delivery date');
        } finally {
            setSaving(false);
        }
    }, [orderId, logisticId, onSaved]);

    if (existing) {
        return (
            <span className="text-sm font-medium text-gray-800">{String(initialDate).trim()}</span>
        );
    }

    return (
        <div className="flex flex-col gap-0.5 min-w-[128px]">
            <input
                type="date"
                disabled={saving}
                className="border border-gray-200 rounded px-1.5 py-1 text-xs text-gray-800 w-[118px] disabled:opacity-60"
                value={date}
                onChange={(e) => {
                    const val = e.target.value;
                    setDate(val);
                    if (!val) return;
                    if (debounceRef.current) clearTimeout(debounceRef.current);
                    debounceRef.current = setTimeout(() => {
                        debounceRef.current = null;
                        persistDeliveryDate(val);
                    }, 350);
                }}
            />
            {saving ? <span className="text-[10px] text-gray-400">Saving…</span> : null}
        </div>
    );
}

function ShippingTrackingDashboard() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ results: [], count: 0, summary: null, filters: null });
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const [status, setStatus] = useState('all');
    const [delayedDispatch, setDelayedDispatch] = useState(false);
    const [startDate, setStartDate] = useState(getFirstDayOfMonth());
    const [endDate, setEndDate] = useState(getToday());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 350);

        return () => clearTimeout(timer);
    }, [searchText]);

    const queryParams = useMemo(() => {
        const params = {
            page: currentPage,
            page_size: pageSize,
            status,
        };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (debouncedSearchText) params.search = debouncedSearchText;
        if (delayedDispatch) params.delayed_dispatch = true;
        return params;
    }, [currentPage, pageSize, startDate, endDate, debouncedSearchText, status, delayedDispatch]);

    const fetchDashboard = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        setLoading(true);

        try {
            const res = await getShippingTrackingDashboard(queryParams, {
                signal: abortControllerRef.current.signal,
            });
            if (res.status === 200) {
                setData({
                    results: Array.isArray(res.data?.results) ? res.data.results : [],
                    count: res.data?.count || 0,
                    next: res.data?.next || null,
                    previous: res.data?.previous || null,
                    summary: res.data?.summary || null,
                    filters: res.data?.filters || null,
                });
            } else {
                toast.error('Unable to fetch shipping tracking data');
            }
        } catch (error) {
            if (error?.name !== 'CanceledError' && error?.message !== 'canceled') {
                console.error('Shipping tracking error:', error);
                toast.error('Unable to fetch shipping tracking data');
            }
        } finally {
            setLoading(false);
        }
    }, [queryParams]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    const handleOrderStatusChange = useCallback(async (item, newStatus) => {
        if (!item?.order_id || !newStatus) return;

        const dispatchCheck = canTransitionToDispatched(item, newStatus, {
            orderType: item.order_type || "B2C",
        });
        if (!dispatchCheck.allowed) {
            toast.error(dispatchCheck.message);
            return;
        }

        try {
            const res = await patchOrder({
                data: { status: newStatus },
                id: item.order_id,
            });
            if (res.status === 200) {
                toast.success('Status updated');
                fetchDashboard();
            } else {
                toast.error('Unable to update status');
            }
        } catch (error) {
            console.error('patchOrder:', error);
            toast.error('Unable to update status');
        }
    }, [fetchDashboard]);

    const copyClipboard = (text, label = 'Copied to clipboard!') => {
        if (!text) return;
        navigator.clipboard.writeText(text)
            .then(() => toast.success(label))
            .catch(() => toast.error('Failed to copy'));
    };

    const handleSearch = (query) => {
        setSearchText(query);
        setCurrentPage(1);
    };

    const handleStatusChange = (value) => {
        setStatus(value || 'all');
        setCurrentPage(1);
    };



    const resetFilters = () => {
        setSearchText('');
        setDebouncedSearchText('');
        setStatus('all');
        setDelayedDispatch(false);
        setStartDate(getFirstDayOfMonth());
        setEndDate(getToday());
        setCurrentPage(1);
    };

    const handlePageSizeChange = (value) => {
        setPageSize(value);
        setCurrentPage(1);
    };

    const summary = data.summary || {};
    const summaryCards = [
        { title: 'Total delivery cost', count: formatInr2(summary.total_delivery_cost ?? 0), icon: FaMoneyBillWave },
        { title: 'Total COD commission', count: formatInr2(summary.total_cod_commission ?? 0), icon: FaCreditCard },
        { title: 'Total', count: summary.total ?? 0, icon: FaBoxOpen },
        { title: 'Yet To Ship', count: summary.yet_to_ship ?? 0, icon: FaBoxOpen },
        { title: 'In Transit', count: summary.in_transit ?? 0, icon: FaTruck },
        { title: 'Delivered', count: summary.delivered ?? 0, icon: FaCheckCircle },
        { title: 'RTO', count: summary.rto ?? 0, icon: FaUndo },
        { title: 'Delayed Dispatch', count: summary.delayed_dispatch ?? 0, icon: FaExclamationTriangle },
    ];

    const headers = [
        { label: 'Order', key: 'order', width: 130 },
        { label: 'Customer', key: 'customer', width: 180 },
        { label: 'Sales Person', key: 'sales_person', width: 170 },
        { label: 'Del. cost / COD', key: 'delivery_fees', width: 110 },
        { label: 'Status', key: 'status', width: 200 },
        { label: 'Shipped', key: 'shipped_date', width: 130 },
        { label: 'Delivery date', key: 'delivery_date', width: 160 },
        { label: 'Tracking', key: 'tracking', width: 190 },
        { label: 'Parcel', key: 'parcel', width: 90 },
        { label: 'Address', key: 'address', width: 260 },
    ];

    const rows = (data.results || []).map((item) => ({
        order: (
            <div className="flex items-center gap-2">
                {item.order_id && (
                    <BiCopy
                        className="cursor-pointer text-blue-600 shrink-0"
                        size={18}
                        title="Copy order ID"
                        onClick={() => copyClipboard(item.order_id, 'Order ID copied!')}
                    />
                )}
                <span className="flex flex-col">
                    <span className="font-medium text-gray-800">{item.order_date || '-'}</span>
                    {formatDays(item.days_since_ordered) && (
                        <span className="text-xs text-gray-500">{formatDays(item.days_since_ordered)} old</span>
                    )}
                </span>
            </div>
        ),
        customer: (
            <div className="flex flex-col">
                <span className="font-semibold text-gray-800">{item.name || '-'}</span>
                <button
                    type="button"
                    onClick={() => copyClipboard(item.phone_number, 'Phone copied!')}
                    className="text-left text-xs text-blue-700 hover:underline"
                >
                    {item.phone_number || '-'}
                </button>
            </div>
        ),
        sales_person: (
            <div className="flex flex-col gap-0.5">
                <span className="font-medium text-gray-800">{item.sales_person || '-'}</span>
                <span className="text-xs text-blue-600 font-medium" title={item.provider || ''}>
                    {item.provider?.trim() ? item.provider : '—'}
                </span>
            </div>
        ),
        delivery_fees: (
            <div className="flex flex-col gap-1 py-0.5">
                <span className="text-sm font-medium text-gray-800 tabular-nums leading-tight">
                    {formatInr2(item.delivery_rate ?? 0)}
                </span>
                <span className="text-xs text-gray-600 tabular-nums leading-tight">
                    COD {formatInr2(item.cod_commission ?? 0)}
                </span>
            </div>
        ),
        status: (
            <div className="min-w-[180px]">
                <Dropdown
                    name="order_status"
                    color={getOrderStatusDropdownColor(item.delivery_status)}
                    options={ORDER_STATUS_DROPDOWN_OPTIONS}
                    placeholder={
                        (item.delivery_status && STATUS_LABELS[item.delivery_status]) ||
                        item.delivery_status?.replace(/_/g, ' ') ||
                        'Status'
                    }
                    value={item.delivery_status}
                    onChange={(value) => handleOrderStatusChange(item, value)}
                    compact
                />
            </div>
        ),
        shipped_date: (
            <span className="flex flex-col">
                <span>{item.shipped_date || '-'}</span>
                {formatDays(item.days_since_shipped) && (
                    <span className={item.is_delayed_dispatch ? 'text-xs font-semibold text-red-600' : 'text-xs text-gray-500'}>
                        {formatDays(item.days_since_shipped)} shipped
                    </span>
                )}
                {item.is_delayed_dispatch && (
                    <span className="text-[10px] font-bold uppercase text-red-600">Delayed</span>
                )}
            </span>
        ),
        delivery_date: (
            <div className="flex flex-col gap-1 min-w-[128px]">
                <DeliveryDateEditor
                    logisticId={item.id}
                    orderId={item.order_id}
                    initialDate={item.delivery_date || ''}
                    onSaved={fetchDashboard}
                />
                {renderTotalDaysToDeliverLine(item.total_days_to_deliver)}
            </div>
        ),
        tracking: (
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => copyClipboard(item.tracking_number || item.tracking_link, 'Tracking copied!')}
                    className="max-w-32 truncate text-blue-700 hover:underline text-left"
                    title={item.tracking_number || item.tracking_link || ''}
                >
                    {item.tracking_number || 'Tracking Link'}
                </button>
                {(item.tracking_number || item.tracking_link) && (
                    <BiCopy
                        className="cursor-pointer text-blue-600 shrink-0"
                        size={18}
                        onClick={() => copyClipboard(item.tracking_number || item.tracking_link, 'Tracking copied!')}
                    />
                )}
                {item.tracking_link && (
                    <a href={item.tracking_link} target="_blank" rel="noopener noreferrer" className="text-green-700 shrink-0">
                        <BiLinkExternal size={18} />
                    </a>
                )}
            </div>
        ),
        parcel: (
            <span className={item.has_multiple_parcels ? 'font-semibold text-orange-700' : 'text-gray-700'}>
                {item.has_multiple_parcels ? `${item.parcel_index}/${item.parcel_count}` : item.parcel_count || 1}
            </span>
        ),
        address: <p className="max-w-64 whitespace-normal leading-snug">{item.address || '-'}</p>,
    }));

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Shipping Tracking' },
            ]} />

            <div className="py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Shipping Tracking</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Tracking records with a tracking number or tracking link. Summary follows order status counts.
                        </p>
                    </div>
                    {data.filters && (
                        <p className="text-xs text-gray-400">
                            Filters: {data.filters.start_date || '-'} to {data.filters.end_date || '-'}
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                    {summaryCards.map((card) => (
                        <Card key={card.title} title={card.title} count={card.count} icon={card.icon} />
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 mb-3">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                    <SearchBar
                        placeholder="Search name, phone, order, tracking, sales person"
                        onSearch={handleSearch}
                        value={searchText}
                        persistToUrl={false}
                    />
                    <Dropdown
                        label="Status"
                        options={STATUS_OPTIONS}
                        value={status}
                        onChange={handleStatusChange}
                        compact
                    />
                    <DateRangePicker
                        compact
                        value={{ start: startDate, end: endDate }}
                        onChange={({ start, end }) => {
                            if (start && end) {
                                if (start > end) return;
                                setStartDate(start);
                                setEndDate(end);
                                setCurrentPage(1);
                                return;
                            }
                            if (!start && !end) {
                                setStartDate(getFirstDayOfMonth());
                                setEndDate(getToday());
                                setCurrentPage(1);
                            }
                        }}
                        placeholder="Choose start & end date"
                    />
                    <button
                        type="button"
                        onClick={() => {
                            setDelayedDispatch((prev) => !prev);
                            setCurrentPage(1);
                        }}
                        className={`inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg border text-sm font-semibold ${
                            delayedDispatch
                                ? 'border-red-200 bg-red-50 text-red-700'
                                : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <FaExclamationTriangle size={16} />
                        Delayed
                    </button>
                    <button
                        type="button"
                        onClick={resetFilters}
                        className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                        <BiRefresh size={20} />
                        Reset
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex w-full flex-row justify-center my-8">
                    <CustomLoading size={100} />
                </div>
            ) : (
                <Table headers={headers} rows={rows} density="comfortable" />
            )}

            <div className="mt-3">
                <Pagination
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalEntries={data.count || 0}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={handlePageSizeChange}
                    data={data}
                />
            </div>
        </>
    );
}

export default ShippingTrackingDashboard;
